import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {exportRemarkingAuditLog, fetchRemarkingAuditLog} from 'api/remarkingAudit';
import {auditColumns} from 'components/common/AuditLog/columns';
import {AuditExportControl} from 'components/common/AuditLog/ExportControl';
import {AuditNavigation} from 'components/common/AuditLog/Navigation';
import {AuditPeriodFilter} from 'components/common/AuditLog/PeriodFilter';
import {AuditReference} from 'components/common/AuditLog/Reference';
import {AuditRoleFilter} from 'components/common/AuditLog/RoleFilter';
import {BreakAllValue, PageContent, Toolbar} from 'components/common/AuditLog/styled';
import {useAuditLog} from 'components/common/AuditLog/useAuditLog';
import {Page} from 'components/common/Page';
import {formatLocalDateTime, translateUserRole} from 'components/pages/RecallAudit/utils';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {
    REMARKING_AUDIT_EVENTS,
    REMARKING_AUDIT_REASONS,
    REMARKING_AUDIT_SOURCES,
} from 'types/remarking-audit';

import {mapRemarkingAuditFilters} from './filters';

export {RemarkingAuditDetailPage} from './Detail';

const selectFilters: Array<{id: string; values: readonly string[]; dictionary: string}> = [
    {
        id: 'remarkingReason',
        values: REMARKING_AUDIT_REASONS,
        dictionary: 'dictionaries.documents.remarking.reasons',
    },
    {
        id: 'sourceFormation',
        values: REMARKING_AUDIT_SOURCES,
        dictionary: 'dictionaries.products.blockingSourceFormation',
    },
];

export const RemarkingAuditLogPage = () => {
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const {
        filters,
        page,
        size,
        data,
        pending,
        setPage,
        setSize,
        changeFilters,
        resetFilters,
    } = useAuditLog(fetchRemarkingAuditLog, mapRemarkingAuditFilters);
    const fields = auditColumns(
        t,
        REMARKING_AUDIT_EVENTS.map((value) => ({
            value,
            label: t(`pages.remarkingAudit.events.${value}`),
        })),
    ).map((field) =>
        field.id === 'eventTimestamp'
            ? {...field, filter: <AuditPeriodFilter />}
            : field.id === 'userRole'
            ? {...field, filter: <AuditRoleFilter />}
            : field,
    );
    fields.push(
        ...['documentId', 'productCode', 'oldMarkingCode', 'newMarkingCode'].map((id) => ({
            id,
            header: t(`pages.remarkingAudit.fields.${id}`),
            width: 240,
            filter: <Input />,
        })),
    );
    fields.push(
        ...selectFilters.map(({id, values, dictionary}) => ({
            id,
            header: t(`pages.remarkingAudit.fields.${id}`),
            width: 220,
            filter: (
                <Select
                    isClearable
                    options={values.map((value) => ({value, label: t(`${dictionary}.${value}`)}))}
                />
            ),
        })),
    );
    const rows = (data?.data || []).map((entry) => ({
        ...entry,
        entry,
        eventId: (
            <Link to={generatePath(ROUTE.REMARKING_AUDIT_DETAIL, {id: entry.eventId})}>
                {entry.eventId}
            </Link>
        ),
        eventType: t(`pages.remarkingAudit.events.${entry.eventType}`, {
            defaultValue: entry.eventType,
        }),
        eventTimestamp: formatLocalDateTime(entry.eventTimestamp),
        userRole: translateUserRole(t, entry.userRole),
        userFullName: entry.userFullName || '—',
        organizationName: entry.organizationName ?? entry.organizationId ?? '—',
        organizationTin: entry.organizationTin || '—',
        documentId: <AuditReference field="documentId" value={entry.documentId} />,
        ...Object.fromEntries(
            selectFilters.map(({id, dictionary}) => {
                const value = entry[id as keyof typeof entry];
                return [
                    id,
                    value ? t(`${dictionary}.${value}`, {defaultValue: String(value)}) : '—',
                ];
            }),
        ),
        productCode: <BreakAllValue>{entry.productCode || '—'}</BreakAllValue>,
        oldMarkingCode: (
            <BreakAllValue>
                {<AuditReference field="oldMarkingCode" value={entry.oldMarkingCode} />}
            </BreakAllValue>
        ),
        newMarkingCode: (
            <BreakAllValue>
                {<AuditReference field="newMarkingCode" value={entry.newMarkingCode} />}
            </BreakAllValue>
        ),
    }));
    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <AuditNavigation>
                    <Toolbar>
                        {permissions.includes(PERMISSION_ALIASES.remarkingAuditLogExport) && (
                            <AuditExportControl
                                fileName="remarking-audit"
                                initialPeriod={filters.eventTimestamp as [Date | null, Date | null]}
                                onExport={(format, period) => {
                                    const params = mapRemarkingAuditFilters({
                                        ...filters,
                                        eventTimestamp: period,
                                    });
                                    return exportRemarkingAuditLog({
                                        ...params,
                                        from: params.from!,
                                        to: params.to!,
                                        page,
                                        size,
                                        format,
                                    });
                                }}
                            />
                        )}
                    </Toolbar>
                </AuditNavigation>
                <Table
                    hideSetting
                    columns={mapTableColumns(fields, filters, changeFilters, resetFilters)}
                    data={rows}
                    pageIndex={page}
                    pageCount={data?.totalPages}
                    totalRowsCount={data?.totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={(value) => {
                        setSize(value);
                        setPage(0);
                    }}
                    isLoading={pending}
                />
            </PageContent>
        </Page>
    );
};
